import { FC, useRef } from "react";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useUsersApi } from "../../http/users/api";
import { formatSalesRoles, User, UserSalesRole } from "../../http/users/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import CustomLink from "../ui/CustomLink";
import Table from "../ui/Table";

const USER_FIELDS = ["id", "fullName", "email", "department", "salesRole", "currentCostCenter"] as const;
type UserField = (typeof USER_FIELDS)[number];
type TeamMember = NestedPick<User, UserField>;

type TeamMembersProps = {
  userId: string;
};

const TeamMembers: FC<TeamMembersProps> = ({ userId }) => {
  const i18n = useI18n();
  const usersApi = useUsersApi();
  const filters = useListFilters({
    isSearchable: true,
    prefix: "team_members",
  });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const query: PaginationQueryWithFields<TeamMember> = {
    ...filters.state,
    q: debouncedValue,
    fields: USER_FIELDS,
  };

  const result = usersApi.useAdminUserTeamMembers(userId, query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <Table<TeamMember>
      filters={filters}
      searchOptions={{
        onChange: () => {
          aborter.current.abort();
          aborter.current = new AbortController();
        },
      }}
      head={
        <>
          <Table.Th title={i18n.t.common.name} fieldName="user.name" order={orderOptions} />
          <Table.Th title={i18n.t.common.email} fieldName="user.email" order={orderOptions} />
          <Table.Th title={i18n.t.common.department} fieldName="user.department" order={orderOptions} />
          <Table.Th title={i18n.t.common.salesRole} fieldName="user.salesRoles" order={orderOptions} />
          <Table.Th title={i18n.t.common.costCenter} fieldName="user.costCenter" order={orderOptions} />
        </>
      }
      renderRow={(item) => (
        <>
          <td>
            <CustomLink to={`/admin/users/${item.id}`}>{item.fullName}</CustomLink>
          </td>
          <td>{item.email ? <CustomLink to={`mailto:${item.email}`}>{item.email}</CustomLink> : null}</td>
          <td>{item.department}</td>
          <td>{item.salesRole ? formatSalesRoles(i18n.t, item.salesRole as UserSalesRole) : null}</td>
          <td>{item.currentCostCenter}</td>
        </>
      )}
      hoverTr={false}
      error={result.error}
      items={data?.items}
      total={data?.total}
    />
  );
};

export default TeamMembers;
